/* Keep the existing Flickity options, controls and first-image height calculation. */
(() => {
  if (window.TCLBannerSlider) {
    window.TCLBannerSlider.init(document);
    return;
  }

  const instances = new Map();
  const roots = (scope) => [
    ...(scope.matches?.('[data-banner-slider]') ? [scope] : []),
    ...scope.querySelectorAll('[data-banner-slider]'),
  ];

  class BannerSlider {
    constructor(root, settings) {
      this.root = root;
      this.settings = settings;
      this.elem = document.getElementById(`slider-${settings.id}`);
      this.flkty = null;
      this.destroyed = false;
      this.frame = null;
      this.timers = new Set();
      this.lastViewportHeight = 0;
      this.originalHeight = this.elem?.style.height || '';
      this.pendingActions = [];
      this.imageListeners = [];
      this.libraryListeners = [];
      this.onClick = this.onClick.bind(this);
      this.start = this.start.bind(this);
      this.syncViewportHeight = this.syncViewportHeight.bind(this);
      root.addEventListener('click', this.onClick);
      root.addEventListener('pointerdown', this.start);
      root.addEventListener('focusin', this.start);

      if (settings.animation !== 'none') {
        if ('IntersectionObserver' in window) {
          this.animationObserver = new IntersectionObserver((entries) => {
            if (this.destroyed) return;
            if (entries.some((entry) => entry.isIntersecting)) root.classList.add('wbs-pack__animation-applied');
          }, { rootMargin: '0px', threshold: 0 });
          this.animationObserver.observe(root);
        } else root.classList.add('wbs-pack__animation-applied');
      }

      const rect = root.getBoundingClientRect();
      if (settings.defer_init && !window.Shopify?.designMode && 'IntersectionObserver' in window
        && !(rect.bottom > 0 && rect.top < window.innerHeight + 500)) {
        this.initObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.start();
        }, { rootMargin: '500px 0px', threshold: 0 });
        this.initObserver.observe(root);
      } else this.start();
    }

    later(callback, delay) {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        if (!this.destroyed) callback();
      }, delay);
      this.timers.add(timer);
    }

    start() {
      if (this.destroyed || this.flkty || !this.elem || !this.root.isConnected) return;
      if (typeof window.Flickity !== 'function') {
        if (!this.libraryListeners.length) {
          document.querySelectorAll('[data-wbs-pack-external-js]').forEach((script) => {
            script.addEventListener('load', this.start);
            this.libraryListeners.push(script);
          });
        }
        return;
      }
      this.initObserver?.disconnect();
      this.libraryListeners.forEach((script) => script.removeEventListener('load', this.start));
      this.libraryListeners = [];
      this.flkty = new window.Flickity(this.elem, {
        cellAlign: 'center',
        autoPlay: this.settings.autoplay,
        draggable: JSON.parse(getComputedStyle(this.root).getPropertyValue('--draggable')),
        contain: true,
        wrapAround: true,
        adaptiveHeight: false,
        setGallerySize: false,
        imagesLoaded: true,
        prevNextButtons: false,
        pageDots: false,
        on: {
          ready: () => this.later(() => this.elem.classList.add('wbs-pack__stretch-height'), 500),
        },
      });

      if (this.settings.slide_height !== 'adapt_image') {
        this.elem.querySelector('.flickity-viewport')?.style.removeProperty('height');
        this.elem.style.removeProperty('height');
      } else {
        const firstSlide = this.elem.querySelector('.wbs-pack__first-slide') || this.elem.querySelector('.wbs-pack__flex-item');
        firstSlide?.querySelectorAll('.banner__media img,.image_banner_one_img img').forEach((image) => {
          image.addEventListener('load', this.syncViewportHeight);
          this.imageListeners.push(image);
          if (typeof image.decode === 'function') image.decode().then(this.syncViewportHeight).catch(() => {});
        });
        this.flkty.on('change', this.syncViewportHeight);
        window.addEventListener('resize', this.syncViewportHeight);
        this.syncViewportHeight();
        this.later(this.syncViewportHeight, 120);
        this.later(this.syncViewportHeight, 360);
      }
      this.flushActions();
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-slider-prev],[data-slider-next]');
      if (!button || button.closest('[data-banner-slider]') !== this.root) return;
      event.preventDefault();
      this.pendingActions.push({ direction: button.hasAttribute('data-slider-prev') ? 'previous' : 'next' });
      this.start();
      this.flushActions();
    }

    selectBlock(event) {
      const cell = event.target.closest?.('.wbs-pack__flex-item');
      const cells = Array.from(this.elem?.querySelectorAll('.wbs-pack__flex-item') || []);
      const index = cells.findIndex((item) => item === cell || item.classList.contains(`wbs-pack__block-${event.detail?.blockId}`));
      if (index < 0) return;
      this.pendingActions.push({ index });
      this.start();
      this.flushActions();
    }

    flushActions() {
      if (!this.flkty) return;
      for (const action of this.pendingActions.splice(0)) {
        if ('index' in action) this.flkty.select(action.index, false, true);
        else this.flkty[action.direction]();
      }
    }

    getRenderedImageHeight(image) {
      if (!image) return 0;
      const height = image.clientHeight || image.getBoundingClientRect().height;
      if (height > 0) return height;
      if (image.naturalWidth && image.naturalHeight) {
        const width = image.clientWidth || image.getBoundingClientRect().width;
        if (width > 0) return Math.round(width * image.naturalHeight / image.naturalWidth);
      }
      return 0;
    }

    getFirstSlideHeight() {
      const first = this.elem.querySelector('.wbs-pack__first-slide') || this.elem.querySelector('.wbs-pack__flex-item');
      if (!first) return 0;
      if (window.matchMedia('(max-width: 749px)').matches) {
        const height = this.getRenderedImageHeight(first.querySelector('.image_banner_one_img img'));
        if (height > 0) return height;
      }
      const width = first.getBoundingClientRect().width;
      const media = first.querySelector('.banner__media');
      const imageWidth = media?.getAttribute('data-image-width');
      const ratio = media?.getAttribute('data-image-aspect-ratio');
      if (width > 0 && imageWidth && ratio) return Math.round(width * (imageWidth / ratio / imageWidth));
      const image = first.querySelector('.banner__media img,.image_banner_one_img img');
      const height = this.getRenderedImageHeight(image);
      if (height > 0) return height;
      return (media || first.querySelector('.image_banner_one_img'))?.getBoundingClientRect().height || 0;
    }

    syncViewportHeight() {
      if (this.destroyed) return;
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.destroyed || !this.flkty) return;
        const viewport = this.elem.querySelector('.flickity-viewport');
        const height = Math.round(this.getFirstSlideHeight());
        if (!viewport || !height || Math.abs(height - this.lastViewportHeight) < 1) return;
        this.lastViewportHeight = height;
        viewport.style.height = `${height}px`;
        this.elem.style.height = `${height}px`;
      });
    }

    destroy() {
      this.destroyed = true;
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('pointerdown', this.start);
      this.root.removeEventListener('focusin', this.start);
      this.initObserver?.disconnect();
      this.animationObserver?.disconnect();
      this.libraryListeners.forEach((script) => script.removeEventListener('load', this.start));
      this.imageListeners.forEach((image) => image.removeEventListener('load', this.syncViewportHeight));
      window.removeEventListener('resize', this.syncViewportHeight);
      this.flkty?.off('change', this.syncViewportHeight);
      this.flkty?.destroy();
      this.flkty = null;
      this.timers.forEach(clearTimeout);
      this.timers.clear();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.pendingActions = [];
      this.elem?.classList.remove('wbs-pack__stretch-height');
      if (this.elem) this.elem.style.height = this.originalHeight;
      this.root.classList.remove('wbs-pack__animation-applied');
    }
  }

  const init = (scope) => roots(scope).forEach((root) => {
    if (instances.has(root)) return;
    const config = Array.from(document.querySelectorAll('[data-wbs-pack]')).find((script) => script.getAttribute('data-wbs-pack') === root.getAttribute('data-banner-slider'));
    if (!config) return;
    const settings = JSON.parse(config.textContent);
    instances.set(root, new BannerSlider(root, settings));
  });
  const dispose = (scope) => {
    for (const [root, instance] of instances) {
      if (root === scope || scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  };
  window.TCLBannerSlider = { init };
  document.addEventListener('shopify:section:load', (event) => {
    dispose(event.target);
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => dispose(event.target));
  document.addEventListener('shopify:section:select', (event) => roots(event.target).forEach((root) => instances.get(root)?.start()));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-banner-slider]');
    if (root) instances.get(root)?.selectBlock(event);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
